
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CarbonProject {
  registry_id: string;
  name: string;
  developer: string;
  type: string;
  methodology: string;
  location: string;
  status: string;
  credits_issued: number;
  credits_retired: number;
  vintage_year?: number;
  description?: string;
  user_id: string;
}

export const useCarbonProjects = (userId?: string) => {
  const [projects, setProjects] = useState<CarbonProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!userId) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('carbon_projects')
          .select(`
            registry_id,
            name,
            developer,
            type,
            methodology,
            location,
            status,
            credits_issued,
            credits_retired,
            vintage_year,
            description,
            user_id
          `)
          .eq('user_id', userId)
          .order('registration_date', { ascending: false });

        if (error) {
          throw error;
        }

        setProjects(data || []);
      } catch (err: any) {
        console.error('Error fetching user projects:', err);
        setError(err.message);
        toast({
          title: "Error",
          description: "Failed to fetch your projects",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [userId, toast]);

  const refetch = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('carbon_projects')
        .select(`
          registry_id,
          name,
          developer,
          type,
          methodology,
          location,
          status,
          credits_issued,
          credits_retired,
          vintage_year,
          description,
          user_id
        `)
        .eq('user_id', userId)
        .order('registration_date', { ascending: false });

      if (error) {
        throw error;
      }

      setProjects(data || []);
    } catch (err: any) {
      console.error('Error refetching user projects:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to refresh your projects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return { projects, loading, error, refetch };
};
